import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UserService } from '../../service/UserService';
import { AuthService } from '../../service/AuthService';

const userService = new UserService();
const authService = new AuthService();

const defaultGridParameters = { page: 0, first: 0, rows: 10, sortField: 'name', sortOrder: 1, filters: {} };

// preset/presetLabel: filtro vindo de um clique num card do dashboard
// ("Online agora", "Risco de churn" etc.) — guardado por aba pra
// sobreviver a essa grid desmontar/remontar (ex: navegar pra "Visualizar"
// e voltar), igual ao gridParameters/search.
// countLoaded: o total (userListCount) já foi buscado — usado no cabeçalho
// da aba para não mostrar "(0)" antes de saber o número.
const defaultTabState = { gridParameters: defaultGridParameters, search: '', preset: null, presetLabel: null, subscriptionStatus: null, medicalServiceId: null, userList: [], userListCount: 0, loaded: false, countLoaded: false };

const initialState = {
  status: 'idle',
  error: null,
  // Qual aba (Alunos=0/Admins=1) estava ativa — sobrevive ao UsersView
  // desmontar/remontar (ex: voltar de "Visualizar"), senão sempre voltava
  // pra Alunos independente de onde o admin estava antes.
  activeIndex: 0,
  // Cada aba (Alunos/Admins) guarda sua própria página/ordenação/busca e os
  // dados já carregados — sobrevive a essa grid desmontar/remontar (ex:
  // navegar pra "Visualizar" e voltar, ou trocar de aba e voltar), então
  // não precisa buscar tudo de novo se já tinha carregado antes. `loaded`
  // controla isso; o botão de recarregar força uma busca de verdade.
  gridState: {
    alunos: { ...defaultTabState },
    admins: { ...defaultTabState },
  },
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getUsersAsync = createAsyncThunk(
  'userList',
  async ({page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters = {}, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null}, { rejectWithValue }) => {
    try {
      return await userService.getUsers(page, first, rows, sortField, sortOrder, filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar usuários');
    }
  }
);

export const loginAsync = createAsyncThunk(
  'login',
  async ({email, password}, { rejectWithValue }) => {
    try {
      return await authService.login(email, password);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao realizar login');
    }
  }
);

export const getUsersCountAsync = createAsyncThunk(
  'userListCount',
  async ({filters = {}, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null}, { rejectWithValue }) => {
    try {
      return await userService.getCountUsers(filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar contagem de usuários');
    }
  }
);

// Só o total de uma aba que ainda não está aberta (cabeçalho das abas) —
// separado do getUsersCountAsync porque não mexe no status de carregamento
// compartilhado, que pertence à grid visível.
export const getTabCountAsync = createAsyncThunk(
  'userTabCount',
  async ({filters = {}, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null}, { rejectWithValue }) => {
    try {
      return await userService.getCountUsers(filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar contagem de usuários');
    }
  }
);

export const sendWelcomeEmailAsync = createAsyncThunk(
  'sendWelcomeEmail',
  async (email, { rejectWithValue }) => {
    try {
      return await userService.sendWelcomeEmail(email);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao enviar email de boas-vindas');
    }
  }
);

export const sendPasswordEmailAsync = createAsyncThunk(
  'sendPasswordEmail',
  async (email, { rejectWithValue }) => {
    try {
      return await userService.sendPasswordEmail(email);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao enviar email de senha');
    }
  }
);

export const resetDevicesAsync = createAsyncThunk(
  'resetDevices',
  async ({userId, gridParameters, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null}, { rejectWithValue }) => {
    try {
      await userService.resetDevices(userId);
      return await userService.getUsers(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao resetar dispositivos');
    }
  }
);

// Ativa/desativa em lote a assinatura de vários alunos selecionados na
// grid (ver bulk-status no subscription.controller.ts do backend) e
// recarrega a página atual da grid em seguida, igual ao padrão já usado
// nos toggles individuais acima.
export const bulkSetSubscriptionStatusAsync = createAsyncThunk(
  'bulkSetSubscriptionStatus',
  async ({userIds, active, reason, gridParameters, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null}, { rejectWithValue }) => {
    try {
      const result = await userService.bulkSetSubscriptionStatus(userIds, active, reason);
      const list = await userService.getUsers(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId);
      return { result, list };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao atualizar assinaturas em lote');
    }
  }
);

// Mapeamento repetido em todo fulfilled que retorna a lista de usuários
// (busca inicial, filtro/ordenação, e refetch após toggle/bulk).
const mapUser = (d) => {
  d['name'] = `${d.firstName} ${d.lastName}`;
  d['createdAtFormatted'] = new Date(d.createdAt).toLocaleDateString('pt-BR');
  d['isVerifiedAccount'] = d.isVerifiedAccount == 1 ? true : false;
  d['residence'] = (d.residences || []).map(it => it.description.split(' - ')[0].trim()).join(', ');
  // Um usuário pode ter vários serviços médicos ao longo do tempo (troca de
  // residência/local) — a coluna mostra só o mais atual, pelo createdAt da
  // tabela de junção (UserMedicalService) que o backend já anexa em cada item.
  const latestService = (d.medicalServices || []).reduce((latest, current) => {
    if (!current?.name) return latest;
    if (!latest) return current;
    return new Date(current.createdAt || 0) > new Date(latest.createdAt || 0) ? current : latest;
  }, null);
  d['service'] = latestService?.name || '';
  // "Ativo" aqui é sobre a assinatura (is_active), não isHidden — mesmo
  // conceito usado no bulk ativar/desativar.
  d['activeStatus'] = (d.subscriptions || []).some(s => s.is_active) ? 'ativo' : 'inativo';
  return d;
};

export const usersSlice = createSlice({
  name: 'usersSlice',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    increment: (state) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      state.value += 1;
    },
    decrement: (state) => {
      state.value -= 1;
    },
    // Use the PayloadAction type to declare the contents of `action.payload`
    incrementByAmount: (state, action) => {
      state.value += action.payload;
    },
    setGridState: (state, action) => {
      const { isAdmin, gridParameters, search, preset = null, presetLabel = null, subscriptionStatus = null, medicalServiceId = null } = action.payload;
      const tab = state.gridState[isAdmin ? 'admins' : 'alunos'];
      tab.gridParameters = gridParameters;
      tab.search = search;
      tab.preset = preset;
      tab.presetLabel = presetLabel;
      tab.subscriptionStatus = subscriptionStatus;
      tab.medicalServiceId = medicalServiceId;
    },
    setActiveIndex: (state, action) => {
      state.activeIndex = action.payload;
    },
    // Ativar/desativar pela tela de detalhes não passa pelos thunks de
    // grid (não tem gridParameters/search pra refazer a busca lá) — isso
    // sincroniza só a linha em cache do aluno, pra não ficar desatualizada
    // se o usuário voltar pro grid sem dar refresh manual.
    setUserActiveStatus: (state, action) => {
      const { userId, activeStatus } = action.payload;
      const row = state.gridState.alunos.userList.find(u => u.id === userId);
      if (row) row.activeStatus = activeStatus;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUsersCountAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetDevicesAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(bulkSetSubscriptionStatusAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUsersAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        const tab = state.gridState[action.meta.arg.isAdmin ? 'admins' : 'alunos'];
        tab.userList = [...action.payload.data || []].map(mapUser);
        tab.loaded = true;
        state.error = null;
      })
      .addCase(getUsersAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getUsersCountAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        const tab = state.gridState[action.meta.arg.isAdmin ? 'admins' : 'alunos'];
        tab.userListCount = action.payload;
        tab.countLoaded = true;
        state.error = null;
      })
      .addCase(getTabCountAsync.fulfilled, (state, action) => {
        const tab = state.gridState[action.meta.arg.isAdmin ? 'admins' : 'alunos'];
        tab.userListCount = action.payload;
        tab.countLoaded = true;
      })
      .addCase(getUsersCountAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(resetDevicesAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        const tab = state.gridState[action.meta.arg.isAdmin ? 'admins' : 'alunos'];
        tab.userList = [...action.payload.data || []].map(mapUser);
        state.error = null;
      })
      .addCase(resetDevicesAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(bulkSetSubscriptionStatusAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        const tab = state.gridState[action.meta.arg.isAdmin ? 'admins' : 'alunos'];
        tab.userList = [...action.payload.list.data || []].map(mapUser);
        state.error = null;
      })
      .addCase(bulkSetSubscriptionStatusAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { increment, decrement, incrementByAmount, setGridState, setActiveIndex, setUserActiveStatus } = usersSlice.actions;

export const selectGridState = (isAdmin) => (state) => state.users.gridState[isAdmin ? 'admins' : 'alunos'];

export const selectActiveIndex = (state) => state.users.activeIndex;

export const selectUserList = (isAdmin) => (state) => state.users.gridState[isAdmin ? 'admins' : 'alunos'].userList;

export const selectUserListCount = (isAdmin) => (state) => state.users.gridState[isAdmin ? 'admins' : 'alunos'].userListCount;

export const selectLoading = (state) => state.users.status === 'loading';

export const selectError = (state) => state.users.error;

export default usersSlice.reducer;